import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PiggyBank, ArrowDown, ArrowUp } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";
import { Sound } from "@/lib/sound";

const PiggyBankModal = ({ open, onClose }) => {
  const { state, depositToPiggy, withdrawFromPiggy } = useGame();
  const [amount, setAmount] = useState("");

  const handleDeposit = () => {
    const n = parseInt(amount, 10);
    if (!n || n <= 0) return toast.error("Enter a positive number.");
    if (n > state.cash) { Sound.error(); return toast.error("You don't have that much cash!"); }
    Sound.pop();
    depositToPiggy(n);
    setAmount("");
    toast.success(`Deposited $${n}! Piggy is happy 🐷`);
  };

  const handleWithdraw = () => {
    const n = parseInt(amount, 10);
    if (!n || n <= 0) return toast.error("Enter a positive number.");
    if (n > state.piggyBank) { Sound.error(); return toast.error("Piggy doesn't have that much!"); }
    Sound.pop();
    withdrawFromPiggy(n);
    setAmount("");
    toast.success(`Withdrew $${n}.`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-3xl border-4 border-blue-300 bg-white p-0 overflow-hidden" data-testid="piggy-modal">
        <div className="bg-gradient-to-br from-blue-300 to-blue-400 p-5 border-b-4 border-blue-500">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <PiggyBank className="w-10 h-10 text-slate-900" />
              <div>
                <div className="text-[11px] uppercase tracking-widest font-black text-blue-900">
                  Savings · +5% per day interest
                </div>
                <DialogTitle className="font-display text-2xl font-bold text-slate-900">
                  Piggy Bank
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-3 text-center">
              <div className="text-xs font-black uppercase text-emerald-700">In wallet</div>
              <div className="font-display text-3xl font-bold text-emerald-800">${state.cash}</div>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-3 text-center">
              <div className="text-xs font-black uppercase text-blue-700">In piggy</div>
              <div className="font-display text-3xl font-bold text-blue-800">${state.piggyBank}</div>
            </div>
          </div>

          <Input
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="rounded-2xl border-2 border-slate-300 text-lg font-bold text-center py-6"
            data-testid="piggy-amount-input"
          />

          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleDeposit}
              data-testid="piggy-bank-deposit-button"
              className="bg-blue-500 hover:bg-blue-600 text-white font-black font-heading rounded-2xl btn-3d border-2 border-blue-600 py-6"
            >
              <ArrowDown className="w-4 h-4 mr-1" /> Deposit
            </Button>
            <Button
              onClick={handleWithdraw}
              data-testid="piggy-bank-withdraw-button"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-black font-heading rounded-2xl btn-3d border-2 border-emerald-600 py-6"
            >
              <ArrowUp className="w-4 h-4 mr-1" /> Withdraw
            </Button>
          </div>

          <p className="text-xs text-center text-slate-500 font-medium">
            🏦 Money in your piggy bank grows +5% each day. Saving pays off!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PiggyBankModal;
